import { motion } from 'framer-motion'
import { Star, Quote } from 'lucide-react'

const TESTIMONIALS = [
  {
    _id: 't-01',
    studentName: 'Karthik Subramanian',
    course: { courseName: 'DevOps Architect' },
    rating: 5,
    content:
      "I was a support engineer stuck in L1 tickets for three years and had almost given up on switching. The Terraform and Jenkins pipeline projects gave me something real to talk about in interviews instead of theory. I joined as a DevOps Engineer with a 70% hike four months after finishing the course.",
    placementDetails: { placed: true, company: 'Zeta', role: 'DevOps Engineer' },
  },
  {
    _id: 't-02',
    studentName: 'Fatima Rizvi',
    course: { courseName: 'Kubernetes Architect' },
    rating: 5,
    content:
      "Kubernetes always felt like a black box until Sudhan sir made us break a cluster and then fix it ourselves. Debugging a failing pod at 10pm on a live lab taught me more than any certification video ever did. I now run production EKS workloads and handle on-call without panic.",
    placementDetails: { placed: true, company: 'Razorpay', role: 'Site Reliability Engineer' },
  },
  {
    _id: 't-03',
    studentName: 'Manoj Deshpande',
    course: { courseName: 'Linux & Networking Essentials' },
    rating: 5,
    content:
      "I came in as a mechanical engineering graduate who had never opened a terminal. The way subnetting, DNS and SSH were connected to actual troubleshooting scenarios made everything click. Six weeks later I cleared my first cloud support interview because I could explain what happens when a packet leaves a server.",
    placementDetails: { placed: true, company: 'Cognizant', role: 'Cloud Support Associate' },
  },
  {
    _id: 't-04',
    studentName: 'Daniel Okonkwo',
    course: { courseName: 'DevOps with AWS & GCP' },
    rating: 5,
    content:
      "Joining from Lagos, I was worried the timezone and remote format would hurt the experience. Every session was recorded, every doubt was answered, and the ArgoCD GitOps module is now the exact workflow my team runs on. Worth every hour I stayed up late for.",
    placementDetails: { placed: true, company: 'Andela', role: 'Platform Engineer' },
  },
  {
    _id: 't-05',
    studentName: 'Sneha Balachandran',
    course: { courseName: 'Cloud Essentials' },
    rating: 5,
    content:
      "As a fresher, the biggest gift was learning AWS, Azure and GCP side by side instead of blindly picking one. When my interviewer asked why I would choose a managed database over a self-hosted one, I had a real answer with cost numbers behind it. Got the offer the same week.",
    placementDetails: { placed: true, company: 'Mphasis', role: 'Associate Cloud Engineer' },
  },
  {
    _id: 't-06',
    studentName: 'Imran Qureshi',
    course: { courseName: 'DevOps Essentials' },
    rating: 5,
    content:
      "I was a manual QA tester and CI/CD was just a word people used in standups. Building a Docker image, pushing it through a GitHub Actions pipeline and watching it deploy on my own was the moment my career changed. My company moved me into the platform team three months later.",
    placementDetails: { placed: true, company: 'Freshworks', role: 'DevOps Engineer' },
  },
  {
    _id: 't-07',
    studentName: 'Ritika Bansal',
    course: { courseName: 'DevOps Architect' },
    rating: 5,
    content:
      "What separates this from every online course I bought and abandoned is that Sudhan sir has 18 years of actual production scars to share. He would show us a Terraform state disaster he lived through and then make us recover from it in the lab. That is the kind of learning that survives an interview panel.",
    placementDetails: { placed: true, company: 'Publicis Sapient', role: 'Senior DevOps Engineer' },
  },
  {
    _id: 't-08',
    studentName: 'Vishnu Prasad Menon',
    course: { courseName: 'Kubernetes Architect' },
    rating: 5,
    content:
      "I joined as a fresher with only a college project on my resume and zero Kubernetes exposure. Helm, ingress, RBAC and cluster upgrades were taught hands-on until they stopped feeling scary. Today I am a Kubernetes Administrator managing clusters for a fintech client, which I genuinely did not think was possible in a year.",
    placementDetails: { placed: true, company: 'Nutanix', role: 'Kubernetes Administrator' },
  },
  {
    _id: 't-09',
    studentName: 'Elena Petrova',
    course: { courseName: 'DevOps with Azure' },
    rating: 5,
    content:
      "I switched from backend development because I enjoyed infrastructure more than writing features. The Azure DevOps YAML pipelines and AKS deployment labs were detailed enough that I contributed to my new team's release process in the first sprint. Clearing AZ-400 afterwards felt almost like revision.",
    placementDetails: { placed: true, company: 'Thoughtworks', role: 'Cloud DevOps Engineer' },
  },
  {
    _id: 't-10',
    studentName: 'Ashwini Kulkarni',
    course: { courseName: 'DevOps Essentials' },
    rating: 4,
    content:
      "Coming back to tech after a five year career break, I needed structure and a lot of patience from a trainer, and I got both. Git, Docker and Jenkins were taught from absolute basics without ever making me feel behind the younger batchmates. I restarted my career as a DevOps Engineer and the confidence matters as much as the salary.",
    placementDetails: { placed: true, company: 'Infosys', role: 'DevOps Engineer' },
  },
]

// Full class strings, not interpolated — Tailwind's JIT only picks up literals it can see in source.
const AVATAR_COLORS = [
  'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
  'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',
]

const getInitials = (name) => {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return '?'
  const first = words[0][0]
  const last = words.length > 1 ? words[words.length - 1][0] : ''
  return (first + last).toUpperCase()
}

// Same name always lands on the same colour, so avatars stay stable across re-renders.
const getAvatarColor = (name) => {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0
  return AVATAR_COLORS[hash % AVATAR_COLORS.length]
}

const Testimonials = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
    <section className="section-padding pb-24 bg-white dark:bg-gray-900">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Student Success Stories
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Hear from our alumni who transformed their careers with Deoriginlabs
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {TESTIMONIALS.map((testimonial, index) => (
            <motion.div
              key={testimonial._id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="card card-hover p-6"
            >
              <div className="flex items-center mb-4">
                <Quote className="w-8 h-8 text-primary-200" />
                <div className="flex ml-auto">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${
                        i < testimonial.rating
                          ? 'text-yellow-500 fill-current'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </div>

              <p className="text-gray-700 dark:text-gray-300 mb-6">
                "{testimonial.content}"
              </p>

              <div className="flex items-center">
                <div
                  aria-hidden="true"
                  className={`w-12 h-12 shrink-0 rounded-full mr-4 flex items-center justify-center font-semibold text-sm ${getAvatarColor(
                    testimonial.studentName
                  )}`}
                >
                  {getInitials(testimonial.studentName)}
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">
                    {testimonial.studentName}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {testimonial.course?.courseName}
                  </p>
                  {testimonial.placementDetails?.placed && (
                    <p className="text-sm text-green-600 font-medium">
                      {testimonial.placementDetails.role} at {testimonial.placementDetails.company}
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  </div>
)

export default Testimonials
